// src/Services/InstructorService/InstructorLearningsService.ts
import httpClient from '@/services/httpClient';
import { transformBackendCourse, transformBackendCourseDetails, BackendCourse, BackendCourseDetails } from '@/utils/courseMapper';
import type { Course, CourseDetails } from '@/services/LearnerService/LearningsService';

// Re-export types from learner service (same structure)
export type {
  Course,
  Lesson,
  CourseModule,
  CourseDetails,
  FAQ,
  Review,
  Instructor as CourseInstructor,
} from '@/services/LearnerService/LearningsService';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Standard API response wrapper from backend
 * All API endpoints return this structure: { success, data, message }
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

/**
 * Lesson Details - Complete lesson data from backend
 */
export interface LessonData {
  id: number;
  title: string;
  lessonType: 'Content-Based' | 'ILTS';
  contentType?: 'document' | 'mp4' | 'scorm' | 'url';
  duration?: string;
  description?: string;

  // Content-Based fields
  lessonContentDocument?: string;
  lesson_content_mp4?: string;
  lesson_content_scorm?: string;
  lesson_content_url?: string;

  // ILTS fields
  iltsType?: 'Online' | 'Offline';
  iltsUrl?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  eventVenue?: string;
  meetUrl?: string;
}

// ============================================================================
// API ENDPOINTS - Instructor specific endpoints
// ============================================================================

const ENDPOINTS = {
  MY_COURSES: '/instructor/courses/instructor/active', // Changed to show courses created by instructor
  WISHLIST: '/instructor/courses/saved',
  BROWSE_COURSES: '/instructor/courses/explore',
  ALL_ACTIVE_COURSES: '/instructor/courses/all-active', // New endpoint for all active courses
  COURSE_DETAILS: (courseId: number) => `/instructor/courses/${courseId}`,
  LESSON_DETAILS: (courseId: number, lessonId: number) => `/instructor/courses/${courseId}/lessons/${lessonId}`,
  SAVE_COURSE: '/instructor/courses/save',
  UNSAVE_COURSE: '/instructor/courses/unsave',
  ENROLL: '/instructor/courses/enroll',
  MARK_LESSON_COMPLETE: (courseId: number, lessonId: number) => `/instructor/courses/${courseId}/lessons/${lessonId}/complete`,
};

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Fetch My Courses for Instructor
 * Returns active (published) courses created by the instructor
 */
export const getMyCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.MY_COURSES);

    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    const courses = response.data.map(course =>
      transformBackendCourse(course, 'my-courses', 'trainer')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch my courses' }
    };
  }
};

/**
 * Fetch Wishlist Courses for Instructor
 * Returns courses saved to the instructor's wishlist
 */
export const getWishlistCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.WISHLIST);

    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    const courses = response.data.map(course =>
      transformBackendCourse(course, 'wishlist', 'trainer')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch wishlist courses' }
    };
  }
};

/**
 * Fetch Browse Courses for Instructor
 * Returns all available courses for browsing
 */
export const getBrowseCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.BROWSE_COURSES);

    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    const courses = response.data.map(course =>
      transformBackendCourse(course, 'browse', 'trainer')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch browse courses' }
    };
  }
};

/**
 * Fetch All Active Courses for Instructor
 * Returns all active courses (status='active' and is_deleted=0) regardless of creator or enrollment
 */
export const getAllActiveCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<BackendCourse[]>>(ENDPOINTS.ALL_ACTIVE_COURSES);

    if (!response.data || response.data.length === 0) {
      return { success: true, data: [], error: null };
    }

    const courses = response.data.map(course =>
      transformBackendCourse(course, 'browse', 'trainer')
    );

    return { success: true, data: courses, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch all active courses' }
    };
  }
};

/**
 * Fetch Course Details for Instructor
 * Returns detailed information about a specific course
 *
 * @param courseId - The ID of the course to fetch
 */
export const getCourseDetails = async (courseId: number): Promise<{
  success: boolean;
  data?: CourseDetails;
  error?: { message: string } | null;
}> => {
  try {
    const endpoint = ENDPOINTS.COURSE_DETAILS(courseId);
    console.log('[InstructorLearningsService] Fetching course details from:', endpoint);

    const response = await httpClient.get<ApiResponse<BackendCourseDetails>>(endpoint);
    console.log('[InstructorLearningsService] Raw API response:', response);

    const courseDetails = transformBackendCourseDetails(response.data);
    console.log('[InstructorLearningsService] Transformed course details:', courseDetails);

    return { success: true, data: courseDetails, error: null };
  } catch (error: any) {
    console.error('[InstructorLearningsService] Error fetching course details:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch course details' }
    };
  }
};

/**
 * Save Course to Wishlist for Instructor
 * Adds a course to the instructor's saved/wishlist
 *
 * @param courseId - The ID of the course to save
 */
export const saveCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.post<ApiResponse<null>>(ENDPOINTS.SAVE_COURSE, { course_id: courseId });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to save course' }
    };
  }
};

/**
 * Remove Course from Wishlist for Instructor
 * Removes a course from the instructor's saved/wishlist
 *
 * @param courseId - The ID of the course to unsave
 */
export const unsaveCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.post<ApiResponse<null>>(ENDPOINTS.UNSAVE_COURSE, { course_id: courseId });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to unsave course' }
    };
  }
};

/**
 * Enroll in Course for Instructor
 * Enrolls the instructor in a specific course
 *
 * @param courseId - The ID of the course to enroll in
 */
export const enrollInCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.post<ApiResponse<null>>(ENDPOINTS.ENROLL, { course_id: courseId });
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to enroll in course' }
    };
  }
};

/**
 * Mark Lesson as Complete for Instructor
 * Marks a lesson as completed by the instructor
 *
 * @param lessonId - The ID of the lesson to mark complete
 * @param courseId - The ID of the course the lesson belongs to
 */
export const markLessonComplete = async (lessonId: number, courseId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.post<ApiResponse<null>>(
      ENDPOINTS.MARK_LESSON_COMPLETE(courseId, lessonId),
      {}
    );
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to mark lesson complete' }
    };
  }
};

/**
 * Fetch Lesson Details for Instructor
 * Returns detailed information about a specific lesson
 *
 * @param courseId - The ID of the course
 * @param lessonId - The ID of the lesson to fetch
 */
export const getLessonDetails = async (courseId: number, lessonId: number): Promise<{
  success: boolean;
  data?: LessonData;
  error?: { message: string } | null;
}> => {
  try {
    const endpoint = ENDPOINTS.LESSON_DETAILS(courseId, lessonId);
    console.log('[InstructorLearningsService] Fetching lesson details from:', endpoint);

    const response = await httpClient.get<ApiResponse<LessonData>>(endpoint);
    console.log('[InstructorLearningsService] Raw lesson API response:', response);

    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    console.error('[InstructorLearningsService] Error fetching lesson details:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch lesson details' }
    };
  }
};
